import type { IntlShape } from 'react-intl';

import type { ExtensionManifest, ToolbarItem } from '@atlaskit/editor-common/extensions';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';

import {
	AGENT_MANAGED_EXTENSION_KEY,
	AGENT_MANAGED_EXTENSION_TYPE_DEFAULT,
	type AgentManagedExtensionPlugin,
} from '../common/agentManagedExtensionPluginType';
import { messages } from '../common/messages';

type ContextualToolbar = NonNullable<ExtensionManifest['modules']['contextualToolbars']>[number];

const customizeIcon = () =>
	import(
		/* webpackChunkName: "@atlaskit-internal_icon-customize" */ '@atlaskit/icon/core/customize'
	).then((mod) => mod.default);

/**
 * Manifest-contributed contextual toolbar items for the agent-managed
 * bodied extension. We only contribute the Customize button; the
 * framework's default extension toolbar provides the rest (layout
 * dropdown, edit, copy, delete).
 *
 * Customize bridges to the plugin by calling the `toggleConfig` action,
 * which dispatches a `TOGGLE_CONFIG` meta transaction via
 * `api.core.actions.execute`.
 */
export const getContextualToolbars = ({
	editorAPI,
	extensionType = AGENT_MANAGED_EXTENSION_TYPE_DEFAULT,
	intl,
}: {
	editorAPI?: PublicPluginAPI<[AgentManagedExtensionPlugin]>;
	extensionType?: string;
	intl: IntlShape;
}): NonNullable<ExtensionManifest['modules']['contextualToolbars']> => {
	const customizeItem: ToolbarItem = {
		key: 'agent-managed-extension-customize',
		label: intl.formatMessage(messages.customize),
		tooltip: intl.formatMessage(messages.customize),
		display: 'icon-and-label',
		icon: customizeIcon,
		action: () => {
			editorAPI?.agentManagedExtension?.actions?.toggleConfig();
			return Promise.resolve();
		},
	};

	const toolbar: ContextualToolbar = {
		context: {
			type: 'extension',
			nodeType: 'bodiedExtension',
			extensionType,
			extensionKey: AGENT_MANAGED_EXTENSION_KEY,
		},
		toolbarItems: [customizeItem],
	};

	return [toolbar];
};
